import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "utils/axios";
import SidePanel from "layouts/SidePanel";
import CategoryActivity from "./components/CategoryActivity";
import DashboardPeriodFilter from "./components/DashboardPeriodFilter";
import InsightsNudges from "./components/InsightsNudges";
import LeakageAnalysis from "./components/LeakageAnalysis";
import ProcurementActivity from "./components/ProcurementActivity";

import ProjectsSpendSummary from "./components/ProjectsSpendSummary";
import SavingsBenchmarks from "./components/SavingsBenchmarks";
import WeeklyActivityStreak from "./components/WeeklyActivityStreak";
import { IconGlyph, themeTokens } from "./components/ProfileDashboardUI";
import { defaultProfileStats, mapDashboardStats, mapProfileUser } from "./profileDashboardData";
import { GET_USERS_DATA_BY_IDS } from "config/services";
import { dashboardService } from "services/dashboardService";

const formatDashboardDate = (value) => {
  if (!value) return "";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "";

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const normalizeUserId = (value) => {
  if (value && typeof value === "object") {
    return value.userId ?? value.id ?? value.managerId ?? value.approverId ?? value.value ?? value._id;
  }
  return value;
};

const collectIdsFromList = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => normalizeUserId(item))
    .map((userId) => Number(userId))
    .filter((userId) => Number.isFinite(userId));

const collectApprovalUserIds = (user) => {
  const userIds = new Set();

  collectIdsFromList(user?.managers || user?.userProfile?.managers || []).forEach((userId) => {
    userIds.add(userId);
  });

  const approvals = user?.userEntity?.approvals || user?.entity?.approvals || [];

  approvals.forEach((approval) => {
    (approval?.businessDivisions || []).forEach((division) => {
      collectIdsFromList(division?.approvers || []).forEach((userId) => {
        userIds.add(userId);
      });
    });
  });

  return Array.from(userIds).filter((userId) => Number.isFinite(userId));
};

const getCurrentUserId = (user) =>
  user?.userId || user?.id || user?.userProfile?.userId || user?.userProfile?.id;

const getCurrentEntityId = (user) =>
  user?.userEntity?.entityId || user?.entity?.entityId || user?.entityId;

const DashboardLoadingOverlay = ({ progress = 1, tokens }) => {
  const bars = [38, 58, 44, 76, 62].map((height, index) => Math.max(10, Math.min(96, (height * progress) / 100 + index * 3)));

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/55 backdrop-blur-[6px]">
      <div
        className="w-[360px] rounded-2xl border bg-white p-6 text-center shadow-[0_24px_70px_rgba(15,23,42,0.18)]"
        style={{ borderColor: tokens.line }}
      >
        <div className="mx-auto mb-5 flex h-24 w-44 items-end justify-center gap-2 rounded-xl bg-[#FFF5ED] px-5 pb-4">
          {bars.map((height, index) => (
            <span
              key={index}
              className="w-5 rounded-t-lg transition-all duration-300 ease-out"
              style={{
                height: `${height}%`,
                backgroundColor: index % 2 === 0 ? "#FD6600" : "#FFCBA0",
              }}
            />
          ))}
        </div>
        <p className="text-[15px] font-bold text-[#111827]">Building your dashboard</p>
        <p className="mt-1 text-[12px] font-medium text-[#6B7280]">
          Fetching procurement activity, savings, and RFQ insights.
        </p>
        <div className="mt-5 h-2 overflow-hidden rounded-full bg-[#EEEDE9]">
          <div
            className="h-full rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%`, backgroundColor: "#FD6600" }}
          />
        </div>
        <p className="mt-2 text-right text-[11px] font-bold text-[#FD6600]">{progress}%</p>
      </div>
    </div>
  );
};

const DashboardPrintStyles = ({ tokens }) => (
  <style>
    {`
      @media print {
        @page {
          size: A4;
          margin: 10mm;
        }

        html,
        body {
          background: #ffffff !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        body * {
          visibility: hidden;
        }

        .dashboard-print-area,
        .dashboard-print-area * {
          visibility: visible;
        }

        .dashboard-print-area {
          position: absolute !important;
          inset: 0 auto auto 0 !important;
          width: 100% !important;
          min-height: auto !important;
          background: #ffffff !important;
          color: #111827 !important;
        }

        .dashboard-no-print {
          display: none !important;
        }

        .dashboard-print-content {
          max-width: none !important;
          padding: 0 !important;
          margin: 0 !important;
        }

        .dashboard-print-content > * {
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .dashboard-print-expand-scroll {
          max-height: none !important;
          overflow: visible !important;
        }

        .dashboard-print-content details:not([open]) > *:not(summary) {
          display: block !important;
        }

        .dashboard-print-content details {
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .dashboard-print-period {
          display: flex !important;
        }
      }

      @media screen {
        .dashboard-print-area {
          background: ${tokens.pageBackground};
        }

        .dashboard-print-period {
          display: none !important;
        }
      }
    `}
  </style>
);

const ProfileDashboardContent = () => {
  const authUser = useSelector((state) => state.auth?.user);
  const businessProfile = useSelector((state) => state.business?.profile);
  const [theme, setTheme] = useState("light");
  const [approvalUserDirectory, setApprovalUserDirectory] = useState({});
  const [dashboardStats, setDashboardStats] = useState(defaultProfileStats);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardProgress, setDashboardProgress] = useState(1);
  const [dashboardError, setDashboardError] = useState("");
  const [dashboardFilter, setDashboardFilter] = useState({ period: "", from: "", to: "" });
  const dashboardRequestRef = useRef(0);

  const tokens = useMemo(() => themeTokens(theme), [theme]);
  const userData = useMemo(
    () => mapProfileUser(authUser, businessProfile, approvalUserDirectory),
    [authUser, businessProfile, approvalUserDirectory],
  );

  useEffect(() => {
    const userIds = collectApprovalUserIds(authUser);

    if (userIds.length === 0) {
      setApprovalUserDirectory({});
      return;
    }

    let isMounted = true;

    const fetchApprovalUsers = async () => {
      try {
        const response = await axios.post(
          GET_USERS_DATA_BY_IDS,
          { userIds },
          { headers: { Authorization: localStorage.getItem("accessToken") || "" } },
        );
        const users = response?.data?.payload || response?.data?.data || [];
        const userDirectory = users.reduce((acc, user) => {
          if (user?.userId) acc[String(user.userId)] = user;
          return acc;
        }, {});

        if (isMounted) setApprovalUserDirectory(userDirectory);
      } catch (error) {
        if (isMounted) setApprovalUserDirectory({});
      }
    };

    fetchApprovalUsers();

    return () => {
      isMounted = false;
    };
  }, [authUser]);

  useEffect(() => {
    if (!dashboardLoading) return undefined;

    const timer = setInterval(() => {
      setDashboardProgress((current) => Math.min(92, current + Math.ceil((92 - current) / 8)));
    }, 220);

    return () => clearInterval(timer);
  }, [dashboardLoading]);

  const loadDashboard = useCallback(async () => {
    const entityId = getCurrentEntityId(authUser);
    const userId = getCurrentUserId(authUser);

    if (!entityId || !userId) {
      setDashboardStats(defaultProfileStats);
      return;
    }

    const requestId = dashboardRequestRef.current + 1;
    dashboardRequestRef.current = requestId;

    try {
      setDashboardLoading(true);
      setDashboardProgress(1);
      setDashboardError("");
      const dashboardPayload = await dashboardService.fetchPurchaseManagerOverview({
        entityId,
        userId,
        scope: "full",
        period: dashboardFilter.period && dashboardFilter.period !== "CUSTOM"
          ? dashboardFilter.period
          : undefined,
        from: dashboardFilter.period === "CUSTOM" ? dashboardFilter.from : undefined,
        to: dashboardFilter.period === "CUSTOM" ? dashboardFilter.to : undefined,
      });
      if (dashboardRequestRef.current !== requestId) return;

      setDashboardStats(mapDashboardStats(dashboardPayload));
      setDashboardProgress(100);
      setTimeout(() => {
        if (dashboardRequestRef.current === requestId) setDashboardLoading(false);
      }, 250);
    } catch (error) {
      if (dashboardRequestRef.current === requestId) {
        setDashboardStats(defaultProfileStats);
        setDashboardError(error?.response?.data?.message || error?.message || "Failed to load dashboard data");
        setDashboardLoading(false);
      }
    }
  }, [authUser, dashboardFilter]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    return () => {
      dashboardRequestRef.current += 1;
    };
  }, []);

  const handleExportDashboard = useCallback(() => {
    window.requestAnimationFrame(() => window.print());
  }, []);

  const openDashboardLink = useCallback((path) => {
    if (!path) return;

    const targetUrl = /^https?:\/\//i.test(path)
      ? path
      : `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
    const openedWindow = window.open(targetUrl, "_blank", "noopener,noreferrer");
    if (openedWindow) openedWindow.opener = null;
  }, []);

  const handlePeriodChange = useCallback((period) => {
    setDashboardFilter({ period, from: "", to: "" });
  }, []);

  const handleCustomPeriodApply = useCallback(({ from, to }) => {
    setDashboardFilter({ period: "CUSTOM", from, to });
  }, []);

  const printPeriodLabel = dashboardFilter.period === "CUSTOM"
    ? `${formatDashboardDate(dashboardFilter.from)} - ${formatDashboardDate(dashboardFilter.to)}`
    : dashboardStats.filters?.period?.label || "All data";

  return (
    <div
      className="dashboard-print-area min-h-screen font-sans"
      style={{ background: tokens.pageBackground, color: tokens.ink }}
    >
      <DashboardPrintStyles tokens={tokens} />
      {dashboardLoading && <DashboardLoadingOverlay progress={dashboardProgress} tokens={tokens} />}
      <nav
        className="dashboard-no-print sticky top-0 z-30 flex items-center justify-between px-5 h-[52px]"
        style={{ backgroundColor: tokens.card, borderBottom: `1px solid ${tokens.line}` }}
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: tokens.brand }}>
            <svg className="w-4 h-4 fill-white" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M3 16V7l7-4 7 4v9H3z" />
              <rect x="7" y="10" width="6" height="6" fill="rgba(0,0,0,.25)" />
            </svg>
          </div>
          <span className="text-[15px] font-semibold tracking-tight">Neevay</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTheme((current) => (current === "light" ? "dark" : "light"))}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition-colors"
            style={{ borderColor: tokens.line, color: tokens.muted, backgroundColor: tokens.card }}
          >
            <IconGlyph name={theme === "light" ? "moon" : "sun"} className="w-3.5 h-3.5" />
            {theme === "light" ? "Dark" : "Light"}
          </button>
          <button
            onClick={() => loadDashboard()}
            disabled={dashboardLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition-colors"
            style={{
              borderColor: tokens.line,
              color: dashboardLoading ? tokens.faint : tokens.muted,
              backgroundColor: tokens.card,
            }}
          >
            <IconGlyph name="refresh" className={`w-3.5 h-3.5 ${dashboardLoading ? "animate-spin" : ""}`} />
            Refresh dashboard
          </button>
          <button
            onClick={handleExportDashboard}
            disabled={dashboardLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition-colors"
            style={{
              borderColor: tokens.line,
              color: dashboardLoading ? tokens.faint : tokens.muted,
              backgroundColor: tokens.card,
            }}
          >
            <IconGlyph name="print" className="w-3.5 h-3.5" /> Export PDF
          </button>
          <div
            className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-[11px] font-semibold"
            style={{
              backgroundColor: tokens.primaryLight,
              borderColor: tokens.primaryMid,
              color: tokens.primary,
            }}
          >
            {userData.initials}
          </div>
        </div>
      </nav>

      <main className="dashboard-print-content w-full max-w-none pb-16 px-4 pt-2 sm:px-5 lg:px-6 xl:px-7">
        {dashboardError && (
          <div
            className="mb-3 rounded-lg border px-4 py-3 text-xs font-medium"
            style={{
              borderColor: tokens.danger,
              backgroundColor: tokens.dangerLight,
              color: tokens.danger,
            }}
          >
            {dashboardError}
          </div>
        )}
        <DashboardPeriodFilter
          value={dashboardFilter.period}
          customRange={dashboardFilter}
          onChange={handlePeriodChange}
          onCustomApply={handleCustomPeriodApply}
          loading={dashboardLoading}
          effectivePeriod={dashboardStats.filters?.period}
          tokens={tokens}
        />
        <div
          className="dashboard-print-period mb-4 items-center justify-between border-b pb-2 text-xs"
          style={{ borderColor: tokens.line, color: tokens.muted }}
        >
          <span className="font-semibold" style={{ color: tokens.ink }}>Reporting period</span>
          <span>{printPeriodLabel}</span>
        </div>
        <ProcurementActivity stats={dashboardStats} tokens={tokens} onNavigate={openDashboardLink} />
        <SavingsBenchmarks stats={dashboardStats} tokens={tokens} />
        <div className="mt-3">
          <WeeklyActivityStreak stats={dashboardStats} tokens={tokens} />
        </div>

        <LeakageAnalysis stats={dashboardStats} tokens={tokens} onNavigate={openDashboardLink} />
        <ProjectsSpendSummary projects={dashboardStats.projects} tokens={tokens} onNavigate={openDashboardLink} />
        <CategoryActivity stats={dashboardStats} tokens={tokens} onNavigate={openDashboardLink} />
        <InsightsNudges stats={dashboardStats} tokens={tokens} onNavigate={openDashboardLink} />

        <div
          className="mt-8 pt-4 flex items-center justify-between flex-wrap gap-2"
          style={{ borderTop: `1px solid ${tokens.line}` }}
        >
          {/* <span className="text-[11px]" style={{ color: tokens.faint }}>
            Neevay - Powered by Tathaatvam Technology Solutions Pvt Ltd
          </span> */}
          <span className="text-[11px]" style={{ color: tokens.faint }}>
            Profile last updated: June 2026
          </span>
        </div>
      </main>
    </div>
  );
};

const MyProfileDashboard = () => (
  <SidePanel>
    <ProfileDashboardContent />
  </SidePanel>
);

export default MyProfileDashboard;
